import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { Alert, Button } from 'antd'
import { InputManager } from '../../InputManager/index.ts'
import { isNewEntry } from '../../FormTable/constants.ts'
import { FieldTypeEnum, SelectTypeEnum } from '../../InputManager/types.ts'
import type {
  ContactFieldConfig,
  CheckboxFieldConfig,
  SelectFieldConfig,
  TextFieldConfig
} from '../../InputManager/types.ts'
import type { TeamMember, RelationshipCategorySelectOption } from './utils.ts'
import type { ColumnsType } from 'antd/es/table'

interface FieldConfigOptions {
  availableCategoryOptions: RelationshipCategorySelectOption[]
  isEditing: boolean
  firstErrorField: string | null
}

interface TeamMemberFieldConfigs {
  contact: ContactFieldConfig
  founder: CheckboxFieldConfig
  category: SelectFieldConfig
  jobTitle: TextFieldConfig
  email: TextFieldConfig
  contactNumber: TextFieldConfig
}

const getTeamMemberFieldConfigs = (record: TeamMember, options: FieldConfigOptions): TeamMemberFieldConfigs => {
  const { availableCategoryOptions, isEditing, firstErrorField } = options

  const contactValue = record.contact || null
  const isNewContact =
    contactValue &&
    (contactValue.value === 'new' ||
      (contactValue.label &&
        typeof contactValue.label === 'string' &&
        contactValue.label.toLowerCase().includes('(new)')))

  return {
    contact: {
      key: 'contact',
      type: FieldTypeEnum.CONTACT,
      label: 'Name',
      value: contactValue,
      required: true,
      placeholder: 'Select contact...',
      size: 'small',
      editable: isEditing,
      showValueTag: false,
      autoFocus: firstErrorField === 'contact',
      hyperlinked: !isNewContact,
      hyperlinkModule: 'contacts',
      getRecordId: (value: any) => {
        if (!value) return null
        return value?.value || value?.id || value?.data?.id || null
      }
    },
    founder: {
      key: 'founder',
      type: FieldTypeEnum.CHECKBOX,
      label: 'Founder',
      value: record.founder || false,
      editable: isEditing,
      autoFocus: firstErrorField === 'founder'
    },
    category: {
      key: 'category',
      type: FieldTypeEnum.SELECT,
      label: 'Job Category',
      value:
        record.category && typeof record.category === 'object'
          ? record.category.value || record.category.id || null
          : record.category || null,
      required: true,
      options: availableCategoryOptions || [],
      placeholder: 'Select category...',
      size: 'small',
      selectType: SelectTypeEnum.FILTER,
      editable: isEditing,
      autoFocus: firstErrorField === 'category'
    },
    jobTitle: {
      key: 'jobTitle',
      type: FieldTypeEnum.TEXT,
      label: 'Job Title',
      value: record.jobTitle || '',
      placeholder: 'Enter job title...',
      size: 'small',
      editable: isEditing,
      autoFocus: firstErrorField === 'jobTitle'
    },
    email: {
      key: 'email',
      type: FieldTypeEnum.TEXT,
      label: 'Email',
      value: record.email || '',
      required: true,
      placeholder: 'Enter email...',
      size: 'small',
      editable: isEditing,
      autoFocus: firstErrorField === 'email'
    },
    contactNumber: {
      key: 'contactNumber',
      type: FieldTypeEnum.TEXT,
      label: 'Contact Number',
      value: record.contactNumber || '',
      placeholder: 'Enter number...',
      size: 'small',
      editable: isEditing,
      autoFocus: firstErrorField === 'contactNumber'
    }
  }
}

interface ColumnConfigOptions {
  label: string
  required: boolean
}

export const getColumnConfig = (
  fieldName: string,
  fieldConfig: ColumnConfigOptions,
  width: number,
  minWidth: number,
  hideRequired: boolean = false
): {
  title: React.ReactNode
  dataIndex: string
  key: string
  width: number
  minWidth: number
} => {
  const showRequired = fieldConfig.required && !hideRequired

  return {
    title: showRequired ? (
      <span>
        <span className='text-red-500'>* </span>
        {fieldConfig.label}
      </span>
    ) : (
      fieldConfig.label
    ),
    dataIndex: fieldName,
    key: fieldName,
    width: width,
    minWidth: minWidth || width
  }
}

interface GetColumnsOptions {
  editingRowKey: string | number | null
  savedEntries: Set<string | number>
  availableCategoryOptions: RelationshipCategorySelectOption[]
  firstErrorFieldKey: { rowKey: string | number; fieldKey: string } | null
  fieldResetKeys: Record<string | number, number>
  validationErrors: Record<string, Record<string, string>>
  emailWarnings: Record<string | number, { contact: any }>
  handleFieldChange: (key: string | number, field: string, value: any) => void
  handleAddContactFromEmail: (key: string | number) => void
  startEditing: (key: string | number) => void
  readOnly: boolean
  hideFounderColumn: boolean
  hideCategoryColumn: boolean
  hideJobTitleColumn: boolean
  hideRequiredMarks: boolean
}

export const getColumns = (options: GetColumnsOptions): ColumnsType<TeamMember> => {
  const {
    editingRowKey,
    savedEntries,
    availableCategoryOptions,
    firstErrorFieldKey,
    fieldResetKeys,
    validationErrors,
    emailWarnings,
    handleFieldChange,
    handleAddContactFromEmail,
    startEditing,
    readOnly,
    hideFounderColumn,
    hideCategoryColumn,
    hideJobTitleColumn,
    hideRequiredMarks
  } = options

  return [
    {
      ...getColumnConfig('contact', { label: 'Name', required: true }, 320, 250, hideRequiredMarks),
      render: (value, record) => {
        const isRowEditing = editingRowKey === record.key
        const isNewRow = isNewEntry(record.key)
        const hasBeenSaved = savedEntries.has(record.key)

        const isEditing = isRowEditing && isNewRow && !hasBeenSaved

        const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
        const fieldConfigs = getTeamMemberFieldConfigs(record, {
          availableCategoryOptions,
          isEditing: isEditing,
          firstErrorField
        })
        const errorKey = `${record.key}-errors`

        if (!isEditing) {
          return (
            <div>
              <InputManager
                fields={[fieldConfigs.contact]}
                inputMode='read'
                labelPosition='none'
                onChange={(key, newValue) => handleFieldChange(record.key, 'contact', newValue)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            </div>
          )
        }

        return (
          <InputManager
            key={`contact-${record.key}-${fieldResetKeys[record.key] || 0}`}
            fields={[fieldConfigs.contact]}
            inputMode='edit'
            labelPosition='none'
            onChange={(key, newValue) => handleFieldChange(record.key, 'contact', newValue)}
            validationErrors={validationErrors[errorKey]}
            textClassName='text-sm'
          />
        )
      }
    },
    ...(hideFounderColumn
      ? []
      : [
          {
            title: 'Founder',
            dataIndex: 'founder',
            key: 'founder',
            width: 80,
            minWidth: 80,
            align: 'center' as const,
            render: (value: TeamMember['founder'], record: TeamMember) => {
              const isEditing = editingRowKey === record.key
              const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
              const fieldConfigs = getTeamMemberFieldConfigs(record, {
                availableCategoryOptions,
                isEditing,
                firstErrorField
              })

              if (!isEditing) {
                return (
                  <div
                    onClick={readOnly ? undefined : () => startEditing(record.key)}
                    className={
                      readOnly
                        ? 'flex justify-center items-center h-4'
                        : 'cursor-pointer flex justify-center items-center h-4'
                    }
                  >
                    {value ? <FontAwesomeIcon icon={faCheck} className='text-green-600 text-xs' /> : null}
                  </div>
                )
              }

              return (
                <div className='flex justify-center items-center h-4'>
                  <InputManager
                    key={`founder-${record.key}-${fieldResetKeys[record.key] || 0}-${record.founder ? 'true' : 'false'}`}
                    fields={[fieldConfigs.founder]}
                    inputMode='edit'
                    labelPosition='none'
                    onChange={(key, newValue) => handleFieldChange(record.key, 'founder', newValue)}
                    textClassName='text-sm'
                  />
                </div>
              )
            }
          }
        ]),
    ...(hideCategoryColumn
      ? []
      : [
          {
            ...getColumnConfig('category', { label: 'Job Category', required: true }, 168, 168, hideRequiredMarks),
            render: (value: TeamMember['category'], record: TeamMember) => {
              const isEditing = editingRowKey === record.key
              const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
              const fieldConfigs = getTeamMemberFieldConfigs(record, {
                availableCategoryOptions,
                isEditing,
                firstErrorField
              })
              const errorKey = `${record.key}-errors`

              if (!isEditing) {
                return (
                  <div
                    onClick={readOnly ? undefined : () => startEditing(record.key)}
                    className={readOnly ? '' : 'cursor-pointer'}
                  >
                    <InputManager
                      fields={[fieldConfigs.category]}
                      inputMode='read'
                      labelPosition='none'
                      onChange={(key, newValue) => handleFieldChange(record.key, 'category', newValue)}
                      validationErrors={validationErrors[errorKey]}
                      textClassName='text-sm'
                    />
                  </div>
                )
              }

              return (
                <InputManager
                  fields={[fieldConfigs.category]}
                  inputMode='edit'
                  labelPosition='none'
                  onChange={(key, newValue) => handleFieldChange(record.key, 'category', newValue)}
                  validationErrors={validationErrors[errorKey]}
                  textClassName='text-sm'
                />
              )
            }
          }
        ]),
    ...(hideJobTitleColumn
      ? []
      : [
          {
            ...getColumnConfig('jobTitle', { label: 'Job Title', required: false }, 140, 140, hideRequiredMarks),
            render: (value: TeamMember['jobTitle'], record: TeamMember) => {
              const isEditing = editingRowKey === record.key
              const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
              const fieldConfigs = getTeamMemberFieldConfigs(record, {
                availableCategoryOptions,
                isEditing,
                firstErrorField
              })

              if (!isEditing) {
                return (
                  <div
                    onClick={readOnly ? undefined : () => startEditing(record.key)}
                    className={readOnly ? '' : 'cursor-pointer'}
                  >
                    <InputManager
                      fields={[fieldConfigs.jobTitle]}
                      inputMode='read'
                      labelPosition='none'
                      onChange={(key, newValue) => handleFieldChange(record.key, 'jobTitle', newValue)}
                      textClassName='text-sm'
                    />
                  </div>
                )
              }

              return (
                <InputManager
                  fields={[fieldConfigs.jobTitle]}
                  inputMode='edit'
                  labelPosition='none'
                  onChange={(key, newValue) => handleFieldChange(record.key, 'jobTitle', newValue)}
                  textClassName='text-sm'
                />
              )
            }
          }
        ]),
    {
      ...getColumnConfig('email', { label: 'Email', required: true }, 180, 180, hideRequiredMarks),
      render: (value, record) => {
        const isEditing = editingRowKey === record.key
        const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
        const fieldConfigs = getTeamMemberFieldConfigs(record, {
          availableCategoryOptions,
          isEditing,
          firstErrorField
        })
        const errorKey = `${record.key}-errors`

        if (!isEditing) {
          return (
            <div
              onClick={readOnly ? undefined : () => startEditing(record.key)}
              className={readOnly ? '' : 'cursor-pointer'}
            >
              <InputManager
                fields={[fieldConfigs.email]}
                inputMode='read'
                labelPosition='none'
                onChange={(key, newValue) => handleFieldChange(record.key, 'email', newValue)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            </div>
          )
        }

        const emailWarning = emailWarnings[record.key]

        return (
          <div>
            <InputManager
              fields={[fieldConfigs.email]}
              inputMode='edit'
              labelPosition='none'
              onChange={(key, newValue) => handleFieldChange(record.key, 'email', newValue)}
              validationErrors={validationErrors[errorKey]}
              textClassName='text-sm'
            />
            {emailWarning && emailWarning.contact && (
              <Alert
                message={
                  <span>
                    Existing contact found: <strong>{emailWarning.contact.name}</strong>{' '}
                    <Button
                      type='link'
                      size='small'
                      onClick={() => handleAddContactFromEmail(record.key)}
                      className='p-0 h-auto text-xs'
                    >
                      Add to entry
                    </Button>
                  </span>
                }
                type='warning'
                showIcon
                closable={false}
                className='mt-1 text-xs'
                style={{ fontSize: '11px', padding: '4px 8px' }}
              />
            )}
          </div>
        )
      }
    },
    {
      ...getColumnConfig('contactNumber', { label: 'Contact Number', required: false }, 140, 140, hideRequiredMarks),
      render: (value, record) => {
        const isEditing = editingRowKey === record.key
        const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
        const fieldConfigs = getTeamMemberFieldConfigs(record, {
          availableCategoryOptions,
          isEditing,
          firstErrorField
        })

        if (!isEditing) {
          return (
            <div
              onClick={readOnly ? undefined : () => startEditing(record.key)}
              className={readOnly ? '' : 'cursor-pointer'}
            >
              <InputManager
                fields={[fieldConfigs.contactNumber]}
                inputMode='read'
                labelPosition='none'
                onChange={(key, newValue) => handleFieldChange(record.key, 'contactNumber', newValue)}
                textClassName='text-sm'
              />
            </div>
          )
        }

        return (
          <InputManager
            fields={[fieldConfigs.contactNumber]}
            inputMode='edit'
            labelPosition='none'
            onChange={(key, newValue) => handleFieldChange(record.key, 'contactNumber', newValue)}
            textClassName='text-sm'
          />
        )
      }
    }
  ]
}
